import { call, put, takeLatest, select } from "redux-saga/effects";
import {
  fetchPagesRequest,
  fetchPagesSuccess,
  fetchPagesFailure,
  fetchPageRequest,
  fetchPageSuccess,
  fetchPageFailure,
  submitPageRequest,
  submitPageSuccess,
  submitPageFailure,
  deletePageRequest,
  deletePageSuccess,
  deletePageFailure,
  createPagesFromTemplateRequest,
  createPagesFromTemplateSuccess,
  createPagesFromTemplateFailure,
  createPageFromTemplateRequest,
  createPageFromTemplateSuccess,
  createPageFromTemplateFailure,
  fetchDraftsRequest,
  fetchDraftsSuccess,
  fetchDraftsFailure,
  createDraftRequest,
  createDraftSuccess,
  createDraftFailure,
  updateDraftRequest,
  updateDraftSuccess,
  updateDraftFailure,
} from "../slices/page.slice";
import { ApiError } from "@/lib/apiClient";
import { ApiResponse, PaginatedApiResponse } from "@/lib/apiClient";
import {
  deletePage,
  getPages,
  getPage,
  postPage,
  putPage,
  createPagesFromTemplate,
  createPageFromTemplate,
  getPageDrafts,
  createPageDraft,
  updatePageDraft,
} from "../actions/page.actions";
import {
  Page,
  CreatePageDraftRequest,
  UpdatePageDraftRequest,
  PageDraft,
  PageDraftResponse,
} from "@/types/models/page";
import { selectSectionsArray } from "../selectors/webbuilder.selectors";

function* fetchPagesSaga(
  action: ReturnType<typeof fetchPagesRequest>,
): Generator<unknown, void, unknown> {
  try {
    const data = (yield call(getPages, {
      page: action.payload.page,
      pageSize: action.payload.pageSize,
    })) as PaginatedApiResponse<Page[]>;

    yield put(
      fetchPagesSuccess({
        list: data.data,
        pagination: data.pagination,
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(fetchPagesFailure(normalized));
  }
}

function* fetchPageSaga(
  action: ReturnType<typeof fetchPageRequest>,
): Generator<unknown, void, unknown> {
  try {
    console.log("🔍 Fetching page with ID:", action.payload.pageId);
    const response = (yield call(getPage, {
      pageId: action.payload.pageId,
    })) as ApiResponse<object>;

    console.log("📥 Raw API response:", response);

    // The apiClient already unwraps response.data.data to response.data
    // So we can access the page directly from response.data
    const page = response.data as Page | undefined;

    if (!page || !page.id) {
      console.error("❌ Invalid page data structure:", response);
      throw new ApiError(
        500,
        "INVALID_RESPONSE",
        "Page data is missing or invalid in API response"
      );
    }

    console.log("📋 Extracted page data:", {
      pageId: page.id,
      pageName: page.name,
      sectionsCount: page.sections?.length || 0,
      sections: page.sections?.map((s) => ({
        id: s.id,
        section_identifier: s.section_identifier,
        sectionKeys: Object.keys(s || {}),
        componentDataKeys: Object.keys(s.component_data || {}),
        inputsKeys: Object.keys((s as any).inputs || {}),
        componentDataSample: s.component_data
          ? Object.keys(s.component_data).slice(0, 2)
          : [],
      })),
    });

    yield put(
      fetchPageSuccess({
        page: page,
      }),
    );
  } catch (err: unknown) {
    console.error("❌ Error fetching page:", err);
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(fetchPageFailure(normalized));
  }
}

// Submit page (create or update)
function* submitPageSaga(
  action: ReturnType<typeof submitPageRequest>,
): Generator<unknown, void, unknown> {
  try {
    const { data, pageId } = action.payload;
    let ret: ApiResponse<object>;
    if (pageId) {
      // Update page
      ret = (yield call(putPage, { pageId, data })) as ApiResponse<object>;
    } else {
      // Create new page - requires full Page object
      ret = (yield call(postPage, {
        data: data as Page,
      })) as ApiResponse<object>;
    }

    // The apiClient already unwraps response.data.data to response.data
    // So we can access the page directly from ret.data
    yield put(
      submitPageSuccess({ data: ret.data as Page }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(submitPageFailure(normalized));
  }
}

// Delete page
function* deletePageSaga(action: ReturnType<typeof deletePageRequest>) {
  try {
    const { pageId } = action.payload;
    yield call(deletePage, { pageId });
    yield put(deletePageSuccess({ pageId }));
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(deletePageFailure(normalized));
  }
}

// Create pages from template
function* createPagesFromTemplateSaga(
  action: ReturnType<typeof createPagesFromTemplateRequest>,
) {
  try {
    const { templateId, pageIds } = action.payload;
    const response = (yield call(createPagesFromTemplate, {
      templateId,
      pageIds,
    })) as ApiResponse<Page[]>;
    
    yield put(
      createPagesFromTemplateSuccess({
        createdPages: response.data,
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(createPagesFromTemplateFailure(normalized));
  }
}

// Create single page from template
function* createPageFromTemplateSaga(
  action: ReturnType<typeof createPageFromTemplateRequest>,
) {
  try {
    const data = (yield call(createPageFromTemplate, {
      pageData: action.payload,
    })) as ApiResponse<Page>;
    yield put(createPageFromTemplateSuccess({ createdPage: data.data }));
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(createPageFromTemplateFailure(normalized));
  }
}

// Draft-related sagas
function* fetchDraftsSaga(
  action: ReturnType<typeof fetchDraftsRequest>,
): Generator<unknown, void, unknown> {
  try {
    console.log("🔍 Fetching drafts for pageId:", action.payload.pageId);
    const response = (yield call(getPageDrafts, {
      pageId: action.payload.pageId,
    })) as ApiResponse<object>;

    console.log("📥 Raw drafts API response:", response);

    // The apiClient already unwraps response.data.data to response.data
    // So we can access the drafts directly from response.data
    const drafts = response.data as PageDraft[];

    console.log("📋 Extracted drafts data:", {
      draftsCount: drafts?.length || 0,
      drafts: drafts?.map((d) => ({
        id: d.id,
        description: d.description,
        created_at: d.created_at,
      })),
    });

    yield put(fetchDraftsSuccess({ drafts: drafts || [] }));
  } catch (err: unknown) {
    console.error("❌ Error fetching drafts:", err);
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(fetchDraftsFailure(normalized));
  }
}

function* createDraftSaga(action: ReturnType<typeof createDraftRequest>) {
  try {
    console.log("💾 Creating draft for pageId:", action.payload.pageId);

    // Get current sections from the store
    const sections: any[] = yield select(selectSectionsArray as any);

    console.log("📋 Current sections for draft:", {
      sectionsCount: sections?.length || 0,
      sections: sections?.map((s: any, index: number) => ({
        id: s.id,
        position: index,
        section_identifier: s.section_identifier,
        componentDataKeys: Object.keys(s.component_data || {}),
      })),
    });

    // Transform sections to the format expected by the API
    const draftSections = sections.map((section: any, index: number) => ({
      section_id: section.id,
      position: index,
      component_data: section.component_data,
      section_inputs: section.section_inputs,
      load_on_first_request: false,
    }));

    const draftData: CreatePageDraftRequest = {
      page_id: action.payload.pageId,
      sections: draftSections,
      description: `Draft created on ${new Date().toLocaleString()}`,
    };

    console.log("📤 Sending draft data to API:", draftData);

    const response = (yield call(createPageDraft, {
      pageId: action.payload.pageId,
      draftData,
    })) as ApiResponse<object>;

    console.log("📥 Raw create draft API response:", response);

    // The apiClient already unwraps response.data.data to response.data
    // So we can access the draft response directly from response.data
    const draftResponse = response.data as PageDraftResponse;

    console.log("📋 Extracted draft response:", {
      draftId: draftResponse.draft.id,
      description: draftResponse.draft.description,
      sectionsCount: draftResponse.sections?.length || 0,
    });

    yield put(createDraftSuccess({ draftResponse }));
  } catch (err: unknown) {
    console.error("❌ Error creating draft:", err);
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(createDraftFailure(normalized));
  }
}

function* updateDraftSaga(action: ReturnType<typeof updateDraftRequest>) {
  try {
    // Get current sections from the store
    const sections: any[] = yield select(selectSectionsArray as any);

    // Transform sections to the format expected by the API
    const draftSections = sections.map((section: any, index: number) => ({
      section_id: section.id,
      position: index,
      component_data: section.component_data,
      section_inputs: section.section_inputs,
      load_on_first_request: false,
    }));

    const draftData: UpdatePageDraftRequest = {
      sections: draftSections,
      description: `Draft updated on ${new Date().toLocaleString()}`,
    };

    const data = (yield call(updatePageDraft, {
      pageId: action.payload.pageId,
      draftId: action.payload.draftId,
      draftData,
    })) as ApiResponse<any>;
    yield put(updateDraftSuccess({ draftResponse: data.data }));
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || String(err),
          );
    yield put(updateDraftFailure(normalized));
  }
}

export default function* pagesRootSaga() {
  yield takeLatest(fetchPagesRequest.type, fetchPagesSaga);
  yield takeLatest(fetchPageRequest.type, fetchPageSaga);
  yield takeLatest(submitPageRequest.type, submitPageSaga);
  yield takeLatest(deletePageRequest.type, deletePageSaga);
  yield takeLatest(
    createPagesFromTemplateRequest.type,
    createPagesFromTemplateSaga,
  );
  yield takeLatest(
    createPageFromTemplateRequest.type,
    createPageFromTemplateSaga,
  );
  // Draft-related sagas
  yield takeLatest(fetchDraftsRequest.type, fetchDraftsSaga);
  yield takeLatest(createDraftRequest.type, createDraftSaga);
  yield takeLatest(updateDraftRequest.type, updateDraftSaga);
}
